// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import { Button, Form } from "react-bootstrap";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionTypeForgotPassword } from "actions";
import { DataState, UserStateAWSError } from "types";
import { tabClickHandler } from "guiutils";
import { UserState } from "states/schedulerstates";
import { isSet } from "utils";

import "./UserPwdForgot.scss";
import { ReactComponent as IconUser } from "static/imgUser.svg";
import LoginErrorStatus from "./LoginErrorStatus";

declare const IS_CLOUD: boolean;

interface IUserPwdForgotExtProps {
  showInfoBox?: boolean;
}
interface IUserPwdForgotProps {
  hasPasswordForgetConfirmCode: boolean;
  dataState: DataState;
  saveResult: string | undefined;
  userEmail: string | undefined;
  awsErrorFeedback: UserStateAWSError | undefined;
}

const mapStateToProps = (state: IState) => {
  const props: IUserPwdForgotProps = {
    hasPasswordForgetConfirmCode: state.scheduler.userState === UserState.PASSWORD_CONFIRM,
    dataState: state.scheduler.loginDataState,
    saveResult: state.gui.login.userStateErrorInfo,
    userEmail: state.gui.login.email,
    awsErrorFeedback: state.gui.login.userStateAWSFeedback,
  };
  return props;
};

interface IUserPwdForgotState {
  username: string;
  confirmationCode: string;
  newPassword: string;
  newPassword2: string;
}

interface IUserPwdForgotDispatch {
  onRequestCode: (state: IUserPwdForgotState) => void;
  onChangePassword: (state: IUserPwdForgotState) => void;
  onLogin: (state: IUserPwdForgotState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IUserPwdForgotDispatch = {
    onRequestCode: (state: IUserPwdForgotState) => {
      const payload: IActionTypeForgotPassword = {
        username: state.username,
        confirmationCode: "",
        newPassword: "",
      };
      dispatch(raiseAction(ActionType.ACT_FORGOT_PASSWORD_CODE_REQ, payload));
    },
    onChangePassword: (state: IUserPwdForgotState) => {
      const payload: IActionTypeForgotPassword = {
        username: state.username,
        confirmationCode: state.confirmationCode,
        newPassword: state.newPassword,
      };
      dispatch(raiseAction(ActionType.ACT_FORGOT_PASSWORD_CHANGE_REQ, payload));
    },
    onLogin: (state: IUserPwdForgotState) => {
      dispatch(raiseAction(ActionType.ACT_BACK_TO_LOGIN, state.username));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({});

class UserPwdForgotComponent extends React.Component<
  IUserPwdForgotProps & IUserPwdForgotExtProps & IUserPwdForgotDispatch & WrappedComponentProps,
  IUserPwdForgotState
> {
  state: IUserPwdForgotState = {
    username: this.props.userEmail ?? "",
    confirmationCode: "",
    newPassword: "",
    newPassword2: "",
  };

  public render() {
    return (
      <div id="user">
        <div id="user_inner">
          <IconUser width={50} />
          <div className="user_right">
            <h1>
              <FormattedMessage id="Reset Password" />
            </h1>
            {this.props.hasPasswordForgetConfirmCode ? (
              <>
                <p>
                  <FormattedMessage id="We've send an email with an verification code to your email address." />
                </p>
                <p>
                  <FormattedMessage id="Please enter that code in the inut box and choose and verify your new password." />
                </p>
              </>
            ) : (
              <>
                <p>
                  <FormattedMessage id="You can request a verification code to be sent to your email address of your account." />
                </p>
                <p>
                  <FormattedMessage id="Afterwards you can enter the received verification code here together with a new password." />
                </p>
              </>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <table>
                <tbody>
                  {(this.props.userEmail === "" || !this.props.hasPasswordForgetConfirmCode) && (
                    <>
                      <tr>
                        <td>
                          <FormattedMessage id="Email Address" />
                        </td>
                        <td>
                          <>
                            <Form.Control
                              type="text"
                              name="username"
                              className={
                                this.state.username.length === 0 ? undefined : "input-error"
                              }
                              value={this.state.username}
                              autoFocus
                              disabled={this.props.dataState === DataState.SAVE_REQ}
                              onChange={(event) => {
                                this.setState({ username: event?.currentTarget.value });
                              }}
                            />
                            {this.props.dataState === DataState.SAVE_FAIL && (
                              <div className="loginError">
                                <FormattedMessage id="Check your Email Address" />
                              </div>
                            )}
                          </>
                          <div className="mb-2"></div>
                        </td>
                      </tr>
                    </>
                  )}

                  {this.props.hasPasswordForgetConfirmCode && (
                    <>
                      <tr>
                        <td>
                          <FormattedMessage id="Verification Code" />:
                        </td>
                        <td>
                          <Form.Control
                            name="confirmationCode"
                            type="text"
                            value={this.state.confirmationCode}
                            disabled={this.props.dataState === DataState.SAVE_REQ}
                            onChange={(event) => {
                              this.setState({ confirmationCode: event?.currentTarget.value });
                            }}
                          />
                          <div className="mb-2"></div>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <FormattedMessage id="New password" />:
                        </td>
                        <td>
                          <Form.Control
                            name="newPassword"
                            type="password"
                            className={
                              this.state.newPassword === this.state.newPassword2 &&
                              this.state.newPassword.length !== 0
                                ? undefined
                                : "input-error"
                            }
                            value={this.state.newPassword}
                            disabled={this.props.dataState === DataState.SAVE_REQ}
                            onChange={(event) => {
                              this.setState({ newPassword: event?.currentTarget.value });
                            }}
                          />
                          {this.props.dataState === DataState.SAVE_FAIL && (
                            <div className="loginError">
                              {this.state.newPassword.length === 0 ? (
                                <FormattedMessage id="Password needed" />
                              ) : this.state.newPassword !== this.state.newPassword2 ? (
                                <FormattedMessage id="Passwords are different" />
                              ) : (
                                ""
                              )}
                            </div>
                          )}

                          <div className="mb-2"></div>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <FormattedMessage id="Verify password" />:
                        </td>
                        <td>
                          <Form.Control
                            name="newPassword2"
                            type="password"
                            className={
                              this.state.newPassword === this.state.newPassword2 &&
                              this.state.newPassword2.length !== 0
                                ? undefined
                                : "input-error"
                            }
                            value={this.state.newPassword2}
                            disabled={this.props.dataState === DataState.SAVE_REQ}
                            onChange={(event) => {
                              this.setState({ newPassword2: event?.currentTarget.value });
                            }}
                          />
                          {this.props.dataState === DataState.SAVE_FAIL && (
                            <div className="loginError">
                              {this.state.newPassword2.length === 0 ? (
                                <FormattedMessage id="Password needed" />
                              ) : this.state.newPassword !== this.state.newPassword2 ? (
                                <FormattedMessage id="Passwords are different" />
                              ) : (
                                ""
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
              {this.props.dataState === DataState.SAVE_FAIL && <LoginErrorStatus />}
            </form>
            <div id="buttonbox" className="mt-2">
              <Button
                id="reqpwdresetcode"
                type="submit"
                variant="success"
                className="me-2"
                disabled={this.state.username.length < 3}
                onClick={(event) => {
                  if (this.props.hasPasswordForgetConfirmCode) {
                    this.props.onChangePassword(this.state);
                  } else {
                    // on request - clear the confirmation-code state-var
                    this.setState({ confirmationCode: "" });
                    this.props.onRequestCode(this.state);
                  }
                }}
              >
                {this.props.hasPasswordForgetConfirmCode ? (
                  <FormattedMessage id="Reset Password" />
                ) : this.props.hasPasswordForgetConfirmCode ? (
                  <FormattedMessage id="Request Verification Code" />
                ) : (
                  <FormattedMessage id="Request New Code" />
                )}
              </Button>
              {this.props.dataState !== DataState.SAVE_REQ && (
                <Button variant="secondary" onClick={(event) => this.props.onLogin(this.state)}>
                  <FormattedMessage id="Cancel" />
                </Button>
              )}
              {IS_CLOUD &&
                this.props.dataState !== DataState.SAVE_REQ &&
                this.props.hasPasswordForgetConfirmCode &&
                (this.props.awsErrorFeedback === UserStateAWSError.CODE_MISMATCH ||
                  this.props.awsErrorFeedback === UserStateAWSError.CODE_MISSING ||
                  this.props.awsErrorFeedback === UserStateAWSError.CODE_EXPIRED) && (
                  <div className="loginOptions">
                    <span
                      tabIndex={0}
                      onKeyDown={tabClickHandler}
                      className="text-link ms-3"
                      onClick={(event) => {
                        this.setState({ confirmationCode: "" });
                        this.props.onRequestCode(this.state);
                      }}
                    >
                      <FormattedMessage id="Resend Code..." />
                    </span>
                  </div>
                )}
            </div>
          </div>
          {this.props.showInfoBox === true && this.props.dataState === DataState.SAVE_REQ && (
            <div className="alert alert-warning mt-2">
              <FormattedMessage id="Changing password..." />
            </div>
          )}
          {this.props.showInfoBox === true && this.props.dataState === DataState.SAVE_FAIL && (
            <div className="alert alert-danger mt-2">
              {this.props.hasPasswordForgetConfirmCode ? (
                <FormattedMessage id="Changing password failed." />
              ) : (
                <FormattedMessage id="Requesting verification code failed." />
              )}
              <br></br>
              {isSet(this.props.saveResult) && <FormattedMessage id={this.props.saveResult} />}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(UserPwdForgotComponent));
